import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Search, Loader2, User, Handshake, Contact as ContactIcon, Home, Building2, CornerDownLeft,
} from "lucide-react"

import { Dialog as DialogPrimitive } from "radix-ui"

import { cn } from "src/utils/cn"
import { Button } from "src/components/ui/button"
import { globalSearch } from "../api/searchApi"

const TYPE_META = {
  lead: { label: "Leads", icon: User },
  deal: { label: "Deals", icon: Handshake },
  contact: { label: "Contacts", icon: ContactIcon },
  booking: { label: "Bookings", icon: Home },
  project: { label: "Projects", icon: Building2 },
}
const TYPE_ORDER = ["lead", "deal", "contact", "booking", "project"]

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState("")
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const abortRef = useRef(null)

  // Reset state each time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery("")
      setResults([])
      setActiveIndex(0)
      // focus after the dialog mounts
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // Debounced search.
  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    const handle = setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      try {
        const res = await globalSearch(q, { signal: controller.signal })
        setResults(res)
        setActiveIndex(0)
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") setResults([])
      } finally {
        setLoading(false)
      }
    }, 220)
    return () => clearTimeout(handle)
  }, [query, open])

  // Flat list (for keyboard nav) + grouped view (for render), preserving order.
  const { flat, groups } = useMemo(() => {
    const byType = {}
    for (const r of results) (byType[r.type] ||= []).push(r)
    const orderedTypes = TYPE_ORDER.filter((t) => byType[t]?.length)
    const flat = []
    const groups = orderedTypes.map((type) => {
      const items = byType[type].map((item) => {
        const index = flat.length
        flat.push(item)
        return { ...item, _index: index }
      })
      return { type, items }
    })
    return { flat, groups }
  }, [results])

  const go = useCallback(
    (item) => {
      if (!item) return
      onOpenChange(false)
      navigate(item.url)
    },
    [navigate, onOpenChange]
  )

  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      go(flat[activeIndex])
    }
  }

  // Keep the active row scrolled into view.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-row="${activeIndex}"]`)
    el?.scrollIntoView({ block: "nearest" })
  }, [activeIndex])

  const showEmpty = !loading && query.trim().length >= 2 && flat.length === 0

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="z-50 bg-slate-900/40"
          style={{ position: "fixed", inset: 0 }}
        />
        {/* Full-screen flex container centers the panel on both axes,
            independent of any Tailwind class compilation. */}
        <DialogPrimitive.Content
          aria-label="Search"
          onKeyDown={onKeyDown}
          onPointerDownOutside={(e) => e.preventDefault()}
          className="z-50 outline-none"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
          onClick={() => onOpenChange(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={cn("flex flex-col gap-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl")}
            style={{
              width: "50vw",
              minWidth: "min(480px, 100%)",
              maxWidth: "100%",
            }}
          >
        <DialogPrimitive.Title className="sr-only">Search</DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">
          Search across leads, deals, contacts, bookings and projects.
        </DialogPrimitive.Description>

        {/* Search input */}
          <div className="flex items-center gap-3 border-b border-slate-100 px-4">
            {loading ? (
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-slate-400" />
            ) : (
              <Search className="h-5 w-5 shrink-0 text-slate-400" />
            )}
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search leads, deals, contacts, bookings…"
              className="w-full bg-transparent text-slate-900 outline-none placeholder:text-slate-400"
              style={{ height: 56, fontSize: 15 }}
            />
            <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-400 sm:block">
              Esc
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="overflow-y-auto p-2" style={{ maxHeight: "55vh" }}>
            {query.trim().length < 2 ? (
              <div className="px-3 py-10 text-center text-[13px] text-slate-400">
                Type at least 2 characters to search.
              </div>
            ) : showEmpty ? (
              <div className="px-3 py-10 text-center text-[13px] text-slate-400">
                No results for “{query.trim()}”.
              </div>
            ) : (
              groups.map((group) => {
                const Meta = TYPE_META[group.type] || {}
                return (
                  <div key={group.type} className="mb-1">
                    <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {Meta.label || group.type}
                    </div>
                    {group.items.map((item) => {
                      const Icon = (TYPE_META[item.type] || {}).icon || Search
                      const active = item._index === activeIndex
                      return (
                        <Button
                          key={`${item.type}-${item.id}`}
                          data-row={item._index}
                          type="button"
                          variant="ghost"
                          onMouseEnter={() => setActiveIndex(item._index)}
                          onClick={() => go(item)}
                          className={cn(
                            "flex h-auto w-full items-center justify-start gap-3 rounded-lg px-3 py-2 text-left font-normal transition-colors",
                            active ? "bg-indigo-50 hover:bg-indigo-50" : "hover:bg-slate-50"
                          )}
                        >
                          <span
                            className={cn(
                              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                              active ? "bg-indigo-100 text-indigo-600" : "bg-slate-100 text-slate-500"
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[14px] font-medium text-slate-800">
                              {item.title}
                            </span>
                            {item.subtitle ? (
                              <span className="block truncate text-[12px] text-slate-400">{item.subtitle}</span>
                            ) : null}
                          </span>
                          {active ? (
                            <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-indigo-400" />
                          ) : null}
                        </Button>
                      )
                    })}
                  </div>
                )
              })
            )}
          </div>

          {/* Footer hint */}
          <div className="flex items-center gap-4 border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1">↑</kbd>
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1">↓</kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1">↵</kbd>
              to open
            </span>
          </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
